import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { BehaviorSubject, Observable, Subject, forkJoin, interval } from 'rxjs';
import { switchMap, takeUntil, takeWhile, tap } from 'rxjs/operators';

import { InvoiceAttachment } from '../../api/models/invoice-attachment';
import { InvoiceAttachmentType } from '../../api/models/invoice-attachment-type';
import { Invoice } from '../../api/models';
import { InvoicesService } from '../../api/services/invoices.service';
import { InvoicePeerService } from '../../service/invoice-peer.service';
import { RealtimeNotificationsService } from '../../service/realtime-notifications.service';

@Component({
  selector: 'app-invoice-attachments-v2-widget',
  templateUrl: './invoice-attachments-v2-widget.component.html',
  styleUrls: ['./invoice-attachments-v2-widget.component.scss'],
})
export class InvoiceAttachmentsV2WidgetComponent implements OnInit, OnDestroy {
  private readonly invoicePeerService = inject(InvoicePeerService);
  private readonly invoicesService = inject(InvoicesService);
  private readonly builder = inject(UntypedFormBuilder);
  private readonly realtime = inject(RealtimeNotificationsService);
  private readonly snackBar = inject(MatSnackBar);

  // AI: Modern API invoice observable, consumed via async pipe in the template.
  public invoice$: Observable<Invoice>;

  // AI: Attachment list managed via BehaviorSubject to avoid reassigning observables.
  private readonly attachmentsSubject = new BehaviorSubject<InvoiceAttachment[]>([]);
  public attachments$ = this.attachmentsSubject.asObservable();

  // AI: Attachment types for the edit and upload form dropdowns.
  private readonly attachmentTypesSubject = new BehaviorSubject<
    InvoiceAttachmentType[]
  >([]);
  public attachmentTypes$ = this.attachmentTypesSubject.asObservable();

  public invoice: Invoice | null = null;
  public selectedItem: InvoiceAttachment | null = null;
  public uploadFiles: File[] = [];
  public isDragOver = false;

  public watermarking = false;
  public watermarkError: string | null = null;

  // AI: Rejections the server returns for an upload (e.g. a non-PDF Original
  // Invoice) are shown here. Reporting them only to the browser console left
  // the operator staring at an unchanged list with no idea why.
  public uploadError: string | null = null;

  public readonly hiddenChoices = [
    { value: true, label: 'Yes' },
    { value: false, label: 'No' },
  ];

  // AI: Edit form for updating attachment metadata.
  public form: UntypedFormGroup;
  // AI: Upload form for selecting attachment type when uploading new files.
  public uploadForm: UntypedFormGroup;
  public locked = false;
  public edited = false;
  public added = false;
  public saving = false;

  private readonly ngDestroy$ = new Subject<void>();

  constructor() {
    // AI: attachment_type_id is required for both editing and uploading.
    this.form = this.builder.group({
      attachment_type_id: [null, Validators.required],
      description: [null],
      is_hidden: [null],
    });
    this.uploadForm = this.builder.group({
      attachment_type_id: [null, Validators.required],
    });
  }

  public ngOnInit(): void {
    // AI: Fetch attachment types once (reference data, rarely changes).
    this.invoicesService.listInvoiceAttachmentTypes().subscribe((types) => {
      // AI: Priority sorting for the dropdown.
      const priority: Record<string, number> = {
        'Original Invoice Attachment': 1,
        'BisnodeRiskGuardianReport': 2,
        'CreditRatingReport': 3,
        'Other': 4,
      };

      const sorted = [...types].sort((a, b) => {
        const pA = priority[a.description] || 5;
        const pB = priority[b.description] || 5;

        if (pA !== pB) {
          return pA - pB;
        }
        return a.description.localeCompare(b.description);
      });

      this.attachmentTypesSubject.next(sorted);

      // AI: Set "Original Invoice Attachment" as default if found.
      const originalType = sorted.find(
        (t) => t.description === 'Original Invoice Attachment',
      );
      if (originalType) {
        this.uploadForm.patchValue({ attachment_type_id: originalType.id });
      }
    });

    // AI: Use getInvoice() for the modern Invoice model (has invoice.id).
    this.invoice$ = this.invoicePeerService.getInvoice().pipe(
      tap((invoice) => {
        this.invoice = invoice;
        if (invoice) {
          this.refreshAttachments(invoice.id);
        } else {
          this.attachmentsSubject.next([]);
        }
      }),
    );

    this.invoicePeerService
      .getState()
      .pipe(takeUntil(this.ngDestroy$))
      .subscribe((state) => {
        this.locked = state === 'locked';
      });

    // AI: Refresh attachment list when another user or backend process creates/updates an attachment.
    this.realtime.events$
      .pipe(takeUntil(this.ngDestroy$))
      .subscribe((event) => {
        if (
          (event.event_type === 'invoice_attachment_created' ||
            event.event_type === 'invoice_attachment_updated') &&
          this.invoice &&
          event.invoice_id === this.invoice.id
        ) {
          this.refreshAttachments(this.invoice.id);
        }
      });
  }

  public ngOnDestroy(): void {
    this.ngDestroy$.next();
    this.ngDestroy$.complete();
  }

  public add(): void {
    this.invoicePeerService.lock();
    this.added = true;
    this.uploadFiles = [];
    this.uploadForm.reset();
    this.uploadError = null;
  }

  public edit(): void {
    if (!this.selectedItem) {
      return;
    }
    this.invoicePeerService.lock();
    // AI: Patch form with the selected item's current values.
    this.form.patchValue({
      attachment_type_id: this.selectedItem.attachment_type_id,
      description: this.selectedItem.description,
      is_hidden: this.selectedItem.is_hidden,
    });
    this.edited = true;
  }

  public revert(): void {
    this.invoicePeerService.unlock();
    this.edited = false;
    this.added = false;
    this.uploadFiles = [];
    this.uploadError = null;
  }

  public save(): void {
    if (!this.invoice || !this.selectedItem) {
      return;
    }
    this.saving = true;
    const request = this.form.value;

    this.invoicesService
      .updateInvoiceAttachment({
        invoiceID: this.invoice.id,
        attachmentID: this.selectedItem.id,
        body: request,
      })
      .subscribe({
        next: () => {
          this.invoicePeerService.unlock();
          this.saving = false;
          this.edited = false;
          this.selectedItem = null;
          if (this.invoice) {
            this.refreshAttachments(this.invoice.id);
          }
        },
        error: (err) => {
          console.error('AI: Failed to update attachment', err);
          this.saving = false;
        },
      });
  }

  public download(attachment: InvoiceAttachment): void {
    if (!this.invoice) {
      return;
    }
    // AI: Get a short-lived signed URL, then open it to trigger browser download.
    this.invoicesService
      .getInvoiceAttachmentFile({
        invoiceID: this.invoice.id,
        attachmentID: attachment.id,
        inline: false,
      })
      .subscribe({
        next: (fileUrl) => {
          window.open(fileUrl.url, '_blank');
        },
        error: (err) => {
          console.error('AI: Failed to get download URL', err);
        },
      });
  }

  public upload(): void {
    if (this.uploadFiles.length === 0 || !this.invoice) {
      return;
    }

    this.saving = true;
    this.uploadError = null;

    const uploads = this.uploadFiles.map((file) => this.uploadFile(file));

    forkJoin(uploads).subscribe({
      next: () => {
        this.invoicePeerService.unlock();
        this.saving = false;
        this.added = false;
        this.uploadFiles = [];
        if (this.invoice) {
          this.refreshAttachments(this.invoice.id);
        }
      },
      error: (err) => {
        // AI: The selected files stay in the list so the operator can drop
        // the offending one and retry without re-picking everything.
        this.uploadError = uploadErrorMessage(err);
        this.saving = false;
      },
    });
  }

  public uploadFileAdded(file: File): void {
    this.uploadFiles.push(file);
  }

  public removeUploadFile(file: File): void {
    this.uploadFiles = this.uploadFiles.filter((f) => f !== file);
  }

  // AI: Handle files selected via the native file picker input.
  public onFileInputChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files) {
      for (let i = 0; i < input.files.length; i++) {
        const file = input.files.item(i);
        if (file) {
          this.uploadFiles.push(file);
        }
      }
    }
    // AI: Reset input so the same file can be selected again if removed.
    input.value = '';
  }

  public onDragOver(event: DragEvent): void {
    event.preventDefault();
    this.isDragOver = true;
  }

  public onDragLeave(): void {
    this.isDragOver = false;
  }

  public onDrop(event: DragEvent): void {
    event.preventDefault();
    this.isDragOver = false;
    if (event.dataTransfer?.files) {
      for (let i = 0; i < event.dataTransfer.files.length; i++) {
        const file = event.dataTransfer.files.item(i);
        if (file) {
          this.uploadFiles.push(file);
        }
      }
    }
  }

  public formatFileSize(bytes: number): string {
    if (bytes < 1024) {
      return bytes + ' B';
    } else if (bytes < 1024 * 1024) {
      return (bytes / 1024).toFixed(1) + ' KB';
    } else {
      return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
    }
  }

  public itemClicked(item: InvoiceAttachment): void {
    this.selectedItem = item !== this.selectedItem ? item : null;
  }

  public watermark(): void {
    if (!this.invoice) {
      return;
    }

    this.watermarking = true;
    this.watermarkError = null;

    this.invoicesService
      .watermarkInvoiceAttachments({ invoiceID: this.invoice.id })
      .pipe(takeUntil(this.ngDestroy$))
      .subscribe({
        next: () => {
          this.snackBar.open(
            'Watermarking started. Watermarked files will appear shortly.',
            'OK',
            { duration: 5000 },
          );
          // AI: Poll for status to detect completion/errors.
          this.pollWatermarkStatus(this.invoice!.id);
        },
        error: (err) => {
          this.watermarking = false;
          if (err.status === 409) {
            this.snackBar.open('Watermarking is already in progress.', 'OK', {
              duration: 3000,
            });
          } else {
            this.snackBar.open('Failed to start watermarking.', 'OK', {
              duration: 5000,
            });
          }
        },
      });
  }

  // AI: Poll the status endpoint to detect completion and report errors.
  private pollWatermarkStatus(invoiceId: number): void {
    const poll = interval(3000).pipe(
      takeUntil(this.ngDestroy$),
      switchMap(() => this.invoicesService.getWatermarkStatus({ invoiceID: invoiceId })),
      takeWhile((status) => status.status === 'RUNNING', true),
    );

    poll.subscribe({
      next: (status) => {
        if (status.status !== 'RUNNING') {
          this.watermarking = false;
          if (status.errors && status.errors.length > 0) {
            const errorMsg = status.errors
              .map((e) => `${e.filename}: ${e.error}`)
              .join('; ');
            this.snackBar.open(`Watermarking completed with errors: ${errorMsg}`, 'OK', {
              duration: 10000,
            });
          } else if (status.status === 'COMPLETED') {
            this.snackBar.open(
              `Watermarking completed. ${status.completed_attachments} file(s) watermarked.`,
              'OK',
              { duration: 5000 },
            );
          } else {
            this.snackBar.open(`Watermarking ${status.status.toLowerCase()}.`, 'OK', {
              duration: 5000,
            });
          }
        }
      },
      error: () => {
        this.watermarking = false;
      },
    });
  }

  private uploadFile(file: File): Observable<InvoiceAttachment> {
    if (!this.invoice) {
      throw new Error('Invoice is not loaded');
    }
    // AI: Upload via multipart/form-data. The generated service handles
    // FormData construction internally via the RequestBuilder.
    // The attachment type is mandatory and comes from the upload form.
    return this.invoicesService.uploadInvoiceAttachment({
      invoiceID: this.invoice.id,
      body: {
        file: file,
        attachment_type_id: this.uploadForm.value.attachment_type_id,
      },
    });
  }

  private refreshAttachments(invoiceId: number): void {
    this.invoicesService
      .listInvoiceAttachments({ invoiceID: invoiceId })
      .subscribe((attachments) => {
        // AI: Filter out hidden, sort newest first.
        const visible = attachments
          .filter((a) => !a.is_hidden)
          .sort(
            (a, b) =>
              new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
          );
        this.attachmentsSubject.next(visible);
      });
  }
}

// AI: uploadErrorMessage extracts the server's explanation from a failed
// upload. cool-api returns { message: "..." } on a rejection; anything else
// falls back to a generic line rather than leaking a raw error object.
function uploadErrorMessage(err: unknown): string {
  const message = (err as { error?: { message?: unknown } })?.error?.message;
  if (typeof message === 'string' && message.trim() !== '') {
    return message;
  }
  return 'Failed to upload attachments.';
}
